/**
 * IoT Shack tenant-label honesty.
 *
 * The Agent / setup page painted a hardcoded `dev` as "Tenant" while
 * cpFetch sent x-tenant-id from selectedTenant / getDefaultTenantId().
 * Display the query tenant. If a local field is a different sandbox
 * override, say so — do not paint that override as the portal tenant.
 * Never invent `dev` when the query tenant is known or absent.
 */

export function normalizeTenantId(value: unknown): string | null {
  if (value == null) return null;
  const tenant = String(value).trim();
  return tenant ? tenant : null;
}

/**
 * Same precedence the page uses for x-tenant-id: session/selected tenant,
 * then the hosted default. Does not invent `dev`.
 */
export function resolveQueryTenant(input: {
  selectedTenant?: string | null;
  hostedTenant?: string | null;
}): string | null {
  return normalizeTenantId(input.selectedTenant) || normalizeTenantId(input.hostedTenant);
}

export function describeTenantLabel(input: {
  queryTenant?: string | null;
  localTenant?: string | null;
}): {
  portalTenant: string | null;
  displayTenant: string | null;
  sandboxNote: string | null;
} {
  const query = normalizeTenantId(input.queryTenant);
  const local = normalizeTenantId(input.localTenant);

  if (local && query && local !== query) {
    return {
      portalTenant: query,
      displayTenant: query,
      sandboxNote: `${local} sandbox — not the portal tenant`,
    };
  }

  return {
    portalTenant: query,
    displayTenant: query || local,
    sandboxNote: null,
  };
}

/** Control-plane header text. Never fall back to a hardcoded `dev`. */
export function portalHeaderTenantText(queryTenant: string | null): string {
  return normalizeTenantId(queryTenant) || '—';
}

export type ListenerEnv = 'dev' | 'prod';

/**
 * Listener create-form Env. Use the session/query tenant.
 * A prod session defaults to prod — never a hardcoded `dev`.
 * Unknown / unset tenant also defaults to prod so a hosted prod
 * walk cannot mint a listener against the wrong env.
 */
export function listenerEnvFromSession(
  queryTenant: string | null | undefined
): ListenerEnv {
  const tenant = normalizeTenantId(queryTenant);
  if (tenant === 'dev') return 'dev';
  return 'prod';
}

/**
 * Tenant used for the same request the label describes.
 * Local override if set, else the query tenant. Never invent `dev`.
 */
export function tenantForRequest(
  localTenant: string | null | undefined,
  queryTenant: string | null
): string | null {
  return normalizeTenantId(localTenant) || normalizeTenantId(queryTenant);
}
